import './App.css'

import { ClockIcon } from '@heroicons/react/outline'
import { format } from 'date-fns'
import { default as GraphemeSplitter } from 'grapheme-splitter'
import { useEffect, useRef, useState } from 'react'
import Div100vh from 'react-div-100vh'

import { AlertContainer } from './components/alerts/AlertContainer'
import { Grid } from './components/grid/Grid'
import { Hints } from './components/hints/Hints'
import { Keyboard } from './components/keyboard/Keyboard'
import { DatePickerModal } from './components/modals/DatePickerModal'
import { InfoModal } from './components/modals/InfoModal'
import { GateReason, InviteGate } from './components/modals/InviteGate'
import { MigrateStatsModal } from './components/modals/MigrateStatsModal'
import { SettingsModal } from './components/modals/SettingsModal'
import {
  StatsModal,
  prefetchDistribution,
} from './components/modals/StatsModal'
import {
  WelcomeOverlay,
  markWelcomeSeen,
} from './components/modals/WelcomeOverlay'
import { Navbar } from './components/navbar/Navbar'
import { preloadSaveTheDate } from './components/savethedate/CardContent'
import { SaveTheDate } from './components/savethedate/SaveTheDate'
import { prefetchLeaderboard } from './components/stats/Leaderboard'
import {
  DATE_LOCALE,
  DISCOURAGE_INAPP_BROWSERS,
  LONG_ALERT_TIME_MS,
  MAX_CHALLENGES,
  REVEAL_TIME_MS,
  WELCOME_INFO_MODAL_MS,
} from './constants/settings'
import {
  CORRECT_WORD_MESSAGE,
  DISCOURAGE_INAPP_BROWSER_TEXT,
  GAME_COPIED_MESSAGE,
  HARD_MODE_ALERT_MESSAGE,
  NOT_ENOUGH_LETTERS_MESSAGE,
  SHARE_FAILURE_TEXT,
  WIN_MESSAGES,
  WORD_NOT_FOUND_MESSAGE,
} from './constants/strings'
import { useAlert } from './context/AlertContext'
import { isInAppBrowser } from './lib/browser'
import { MOCK_GUESS_COUNT, MOCK_STATS } from './lib/devMock'
import {
  captureTokenFromUrl,
  hasToken,
  healStoredToken,
  looksLikeValidToken,
  validateToken,
} from './lib/identity'
import { submitScore } from './lib/leaderboard'
import {
  getPuzzleElapsedMs,
  getStoredIsHighContrastMode,
  loadGameStateFromLocalStorage,
  markPuzzleStarted,
  saveGameStateToLocalStorage,
  setStoredIsHighContrastMode,
} from './lib/localStorage'
import { restoreProgress, saveProgress } from './lib/progress'
import { addStatsForCompletedGame, loadStats } from './lib/stats'
import {
  findFirstUnusedReveal,
  getGameDate,
  getIsLatestGame,
  isWinningWord,
  isWordInWordList,
  setGameDate,
  solution,
  solutionGameDate,
  solutionIndex,
  unicodeLength,
} from './lib/words'

function App() {
  // Capture a signed guest token from ?t= on first load (before anything reads
  // identity), then strip it from the URL. Module-safe: runs once per load.
  captureTokenFromUrl()
  // Repair any previously-stored token that carries a stray paste artifact
  // (e.g. a trailing backtick) so an already-affected guest doesn't have to
  // re-open their link. No-op for clean tokens.
  healStoredToken()

  // In-app invite gate: with no valid-looking token, show the "invite required"
  // screen instead of the game. UX/exclusivity only — the server write-gate is
  // the real enforcement. Set REACT_APP_REQUIRE_INVITE='0' to bypass (dev).
  // NOTE: computed as a flag (not an early return) so the hooks below always
  // run in the same order — the branch happens in the render output.
  const requireInvite = process.env.REACT_APP_REQUIRE_INVITE !== '0'
  const gated = requireInvite && !MOCK_STATS && !looksLikeValidToken()

  const isLatestGame = getIsLatestGame()
  const gameDate = getGameDate()
  const prefersDarkMode = window.matchMedia(
    '(prefers-color-scheme: dark)'
  ).matches

  const {
    showError: showErrorAlert,
    showSuccess: showSuccessAlert,
    hide: hideAlert,
  } = useAlert()
  const [currentGuess, setCurrentGuess] = useState('')
  const [isGameWon, setIsGameWon] = useState(false)
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false)
  const [isStatsModalOpen, setIsStatsModalOpen] = useState(
    !!MOCK_STATS && MOCK_STATS !== 'card'
  )
  // Open the envelope on the very first paint when this browser already has a
  // finished game, so a returning guest never sees the board and then a jump
  // to the envelope. (A finish restored from the server on a new device still
  // arrives a moment later.)
  const [isSaveTheDateOpen, setIsSaveTheDateOpen] = useState(() => {
    if (MOCK_STATS === 'card') return true
    const saved = loadGameStateFromLocalStorage(isLatestGame)
    if (!saved || saved.solution !== solution) return false
    return (
      saved.guesses.includes(solution) ||
      saved.guesses.length === MAX_CHALLENGES
    )
  })
  // Bumped on every open so a fresh, sealed envelope mounts even if the last
  // one is still tucking itself away behind the leaderboard.
  const [saveTheDateKey, setSaveTheDateKey] = useState(0)
  // Opened from the leaderboard button (animated hand-off) vs. on page load
  // (instant).
  const [saveTheDateHandoff, setSaveTheDateHandoff] = useState(false)
  // While the card is up, fetch the leaderboard so it opens fully drawn.
  // Load the Save the Date's card and photos in the background a few
  // seconds in, once the game itself has finished loading.
  useEffect(() => {
    const t = window.setTimeout(preloadSaveTheDate, 2500)
    return () => window.clearTimeout(t)
  }, [])

  useEffect(() => {
    if (isSaveTheDateOpen) prefetchLeaderboard()
  }, [isSaveTheDateOpen])

  // True when the leaderboard is reopened from the card's ✕: show it with no
  // fade, since the screen is already dark (avoids a dark-light-dark dip).
  const [statsFromCard, setStatsFromCard] = useState(false)
  const [isDatePickerModalOpen, setIsDatePickerModalOpen] = useState(false)
  const [isMigrateStatsModalOpen, setIsMigrateStatsModalOpen] = useState(false)
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false)
  const [currentRowClass, setCurrentRowClass] = useState('')
  const [isGameLost, setIsGameLost] = useState(false)
  const [isDarkMode, setIsDarkMode] = useState(
    localStorage.getItem('theme')
      ? localStorage.getItem('theme') === 'dark'
      : prefersDarkMode
      ? true
      : false
  )
  const [isHighContrastMode, setIsHighContrastMode] = useState(
    getStoredIsHighContrastMode()
  )
  // Server verdict on the stored token: null = not yet probed. When a token is
  // present we probe GET /api/progress once on load; a 401 means the link is
  // stale/corrupt and the guest is shown the 'rejected' invite screen instead
  // of being allowed to play into a submit wall.
  const [tokenRejected, setTokenRejected] = useState(false)
  const [isRevealing, setIsRevealing] = useState(false)
  // True when the page opens on a game that's already over (restored from this
  // browser or the server). Returning guests skip the finish toast and go
  // straight to the Save the Date card; "Back to leaderboard" is one tap away.
  const finishedOnLoad = useRef(false)
  // A signed guest whose browser has no saved game for today (new device,
  // cleared data, private window) may already have played: wait for the
  // server copy before drawing anything, so a finished guest goes straight
  // to the envelope instead of seeing the intro popup first. Max ~3s.
  const [restoring, setRestoring] = useState(() => {
    if (!hasToken()) return false
    const saved = loadGameStateFromLocalStorage(isLatestGame)
    return !(saved && saved.solution === solution && saved.guesses.length > 0)
  })
  const [guesses, setGuesses] = useState<string[]>(() => {
    const loaded = loadGameStateFromLocalStorage(isLatestGame)
    if (loaded?.solution !== solution) {
      return []
    }
    const gameWasWon = loaded.guesses.includes(solution)
    if (gameWasWon) {
      finishedOnLoad.current = true
      setIsGameWon(true)
    }
    if (loaded.guesses.length === MAX_CHALLENGES && !gameWasWon) {
      finishedOnLoad.current = true
      setIsGameLost(true)
    }
    return loaded.guesses
  })

  const [stats, setStats] = useState(() => loadStats())

  const [isHardMode, setIsHardMode] = useState(
    localStorage.getItem('gameMode')
      ? localStorage.getItem('gameMode') === 'hard'
      : false
  )

  useEffect(() => {
    // On first visit with no saved game, show the how-to info modal — but ONLY
    // for anonymous/dev play. Signed-in guests get the WelcomeOverlay as their
    // intro (which has its own "how to play" link), so auto-popping How-To-Play
    // on top of it — and again on every identity switch — is redundant.
    if (!hasToken() && !loadGameStateFromLocalStorage(true)) {
      setTimeout(() => {
        setIsInfoModalOpen(true)
      }, WELCOME_INFO_MODAL_MS)
    }
  })

  useEffect(() => {
    DISCOURAGE_INAPP_BROWSERS &&
      isInAppBrowser() &&
      showErrorAlert(DISCOURAGE_INAPP_BROWSER_TEXT, {
        persist: false,
        durationMs: 7000,
      })
  }, [showErrorAlert])

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }

    if (isHighContrastMode) {
      document.documentElement.classList.add('high-contrast')
    } else {
      document.documentElement.classList.remove('high-contrast')
    }
  }, [isDarkMode, isHighContrastMode])

  const handleDarkMode = (isDark: boolean) => {
    setIsDarkMode(isDark)
    localStorage.setItem('theme', isDark ? 'dark' : 'light')
  }

  const handleHardMode = (isHard: boolean) => {
    if (guesses.length === 0 || localStorage.getItem('gameMode') === 'hard') {
      setIsHardMode(isHard)
      localStorage.setItem('gameMode', isHard ? 'hard' : 'normal')
    } else {
      showErrorAlert(HARD_MODE_ALERT_MESSAGE)
    }
  }

  const handleHighContrastMode = (isHighContrast: boolean) => {
    setIsHighContrastMode(isHighContrast)
    setStoredIsHighContrastMode(isHighContrast)
  }

  const clearCurrentRowClass = () => {
    setCurrentRowClass('')
  }

  useEffect(() => {
    saveGameStateToLocalStorage(getIsLatestGame(), { guesses, solution })
  }, [guesses])

  // Validate the stored token against the server ONCE on load. The client holds
  // no secret, so this is the only way to know a link is genuinely good. A 401
  // verdict ('invalid') means the token is stale/corrupt (e.g. a stray
  // character pasted into the link, or signed with an older secret) — show the
  // 'rejected' invite gate rather than letting the guest play then fail at
  // submit. A network error ('unknown') is NOT treated as rejection, so a flaky
  // connection never falsely locks a guest out.
  useEffect(() => {
    if (!hasToken()) return
    let cancelled = false
    ;(async () => {
      const verdict = await validateToken()
      if (!cancelled && verdict === 'invalid') {
        setTokenRejected(true)
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Server-authoritative restore: when a signed guest token is present, fetch
  // this guest's saved board from the server and rehydrate it. This is what
  // makes the board survive a browser-data clear and follow the guest across
  // devices — the server copy wins over (empty) localStorage. Runs once.
  useEffect(() => {
    if (!hasToken()) return
    let cancelled = false
    // Never hold the page for a slow network.
    const giveUp = window.setTimeout(() => setRestoring(false), 3000)
    ;(async () => {
      try {
        await restoreFromServer()
      } finally {
        window.clearTimeout(giveUp)
        if (!cancelled) setRestoring(false)
      }
    })()
    async function restoreFromServer() {
      const prog = await restoreProgress()
      if (cancelled || !prog) return
      // The answer is the same every day (YALLS), so a game saved on any
      // day counts: guests get one play, not one per day.
      if (!Array.isArray(prog.guesses) || prog.guesses.length === 0) return
      // Already started on another device: skip the intro here too.
      markWelcomeSeen()
      setGuesses(prog.guesses)
      const serverWon = prog.won === true || prog.guesses.includes(solution)
      if (serverWon) {
        finishedOnLoad.current = true
        setIsGameWon(true)
      } else if (prog.finished || prog.guesses.length === MAX_CHALLENGES) {
        finishedOnLoad.current = true
        setIsGameLost(true)
      }
    }
    return () => {
      cancelled = true
      window.clearTimeout(giveUp)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Persist guesses to the server after each change (clear-proof, cross-device).
  // Fire-and-forget; local play is unaffected if the network call fails.
  useEffect(() => {
    if (!hasToken()) return
    if (guesses.length === 0) return
    const won = guesses.includes(solution)
    const finished = won || guesses.length === MAX_CHALLENGES
    void saveProgress({
      day: solutionIndex,
      guesses,
      finished,
      won,
    })
  }, [guesses])

  // Open the stats modal only AFTER the result has been persisted + submitted
  // server-side, so the leaderboard and distribution both fetch a DB that
  // already contains this game (no "No games recorded yet" race). For a signed
  // guest we explicitly: finish-save (freezes the row + computes time) -> submit
  // (write-once) -> THEN open. We open in finally() so a network failure never
  // traps the modal shut. Anonymous/dev play (no token) keeps the old timing.
  useEffect(() => {
    if (!isGameWon && !isGameLost) return

    const revealMs = REVEAL_TIME_MS * solution.length

    // Start the network work the moment the game ends, while the last row is
    // still flipping and the toast is up: save + submit this game, then fetch
    // the leaderboard and distribution (now including it). By the time the
    // window opens it's fully drawn at its final size: no "Loading..." and no
    // jump in height. Nothing here ever blocks the window for long.
    const ready = (async () => {
      if (hasToken()) {
        try {
          // Freeze the finished row (computes time_ms) before submitting,
          // since submit reads that row.
          await saveProgress({
            day: solutionIndex,
            guesses,
            finished: true,
            won: isGameWon,
          })
          await submitScore({ day: solutionIndex })
        } catch {
          // Non-fatal: the Leaderboard shows its own error state.
        }
      }
      await Promise.all([prefetchLeaderboard(), prefetchDistribution()])
    })()

    // Open once the data is in. The 2.5s budget counts from the end of the
    // game, so after the ~2s toast there's at most a short wait (normally
    // none); a slow network never leaves the guest staring at the board.
    const deadline = Date.now() + 2500
    const persistThenOpen = async (
      open: () => void = () => setIsStatsModalOpen(true)
    ) => {
      const wait = Math.max(0, deadline - Date.now())
      await Promise.race([
        ready,
        new Promise((resolve) => setTimeout(resolve, wait)),
      ])
      open()
    }

    // Returning to a finished game: no toast, no reveal wait. Make sure the
    // result is saved/submitted (both are idempotent).
    if (finishedOnLoad.current) {
      // Show the envelope right away (it fades in over the board); the
      // save/submit happens quietly in the background.
      setIsSaveTheDateOpen(true)
      void persistThenOpen(() => {})
      return
    }

    if (isGameWon) {
      const winMessage =
        WIN_MESSAGES[Math.min(Math.max(guesses.length, 1), 6) - 1]
      // Show the win toast; when it closes, persist + submit, then open stats.
      showSuccessAlert(winMessage, {
        delayMs: revealMs,
        onClose: () => {
          void persistThenOpen()
        },
      })
      return
    }

    // Lost: wait out the reveal, then persist + submit, then open stats.
    const id = setTimeout(() => {
      void persistThenOpen()
    }, (solution.length + 1) * REVEAL_TIME_MS)
    return () => clearTimeout(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGameWon, isGameLost, showSuccessAlert])

  // When the stats modal (leaderboard) opens, clear any lingering alert toast
  // (e.g. the persistent "The word was YALLS" message) so it doesn't overlap
  // the modal. Does not affect the welcome overlay.
  useEffect(() => {
    if (isStatsModalOpen) {
      hideAlert()
    }
  }, [isStatsModalOpen, hideAlert])

  const onChar = (value: string) => {
    if (
      unicodeLength(`${currentGuess}${value}`) <= solution.length &&
      guesses.length < MAX_CHALLENGES &&
      !isGameWon
    ) {
      setCurrentGuess(`${currentGuess}${value}`)
    }
  }

  const onDelete = () => {
    setCurrentGuess(
      new GraphemeSplitter().splitGraphemes(currentGuess).slice(0, -1).join('')
    )
  }

  const onEnter = () => {
    if (isGameWon || isGameLost) {
      return
    }

    if (!(unicodeLength(currentGuess) === solution.length)) {
      setCurrentRowClass('jiggle')
      return showErrorAlert(NOT_ENOUGH_LETTERS_MESSAGE, {
        onClose: clearCurrentRowClass,
      })
    }

    if (!isWordInWordList(currentGuess)) {
      setCurrentRowClass('jiggle')
      return showErrorAlert(WORD_NOT_FOUND_MESSAGE, {
        onClose: clearCurrentRowClass,
      })
    }

    // enforce hard mode - all guesses must contain all previously revealed letters
    if (isHardMode) {
      const firstMissingReveal = findFirstUnusedReveal(currentGuess, guesses)
      if (firstMissingReveal) {
        setCurrentRowClass('jiggle')
        return showErrorAlert(firstMissingReveal, {
          onClose: clearCurrentRowClass,
        })
      }
    }

    setIsRevealing(true)
    // turn this back off after all
    // chars have been revealed
    setTimeout(() => {
      setIsRevealing(false)
    }, REVEAL_TIME_MS * solution.length)

    const winningWord = isWinningWord(currentGuess)

    if (
      unicodeLength(currentGuess) === solution.length &&
      guesses.length < MAX_CHALLENGES &&
      !isGameWon
    ) {
      markPuzzleStarted(solutionIndex)
      setGuesses([...guesses, currentGuess])
      setCurrentGuess('')

      if (winningWord) {
        if (isLatestGame) {
          setStats(addStatsForCompletedGame(stats, guesses.length))
        }
        return setIsGameWon(true)
      }

      if (guesses.length === MAX_CHALLENGES - 1) {
        if (isLatestGame) {
          setStats(addStatsForCompletedGame(stats, guesses.length + 1))
        }
        setIsGameLost(true)
        showErrorAlert(CORRECT_WORD_MESSAGE(solution), {
          persist: true,
          delayMs: REVEAL_TIME_MS * solution.length + 1,
        })
      }
    }
  }

  // Invite gate (computed above): show the "invite required" screen instead of
  // the game when no valid-looking token is present, OR the "this link didn't
  // work" screen when a token was present but the server refused it. All hooks
  // have already run.
  if (gated || tokenRejected) {
    const reason: GateReason = tokenRejected ? 'rejected' : 'no-invite'
    return <InviteGate reason={reason} />
  }

  // Checking the server for this guest's game (see `restoring`): show just
  // the page background, then the right screen appears in one step.
  if (restoring) {
    return <Div100vh />
  }

  return (
    <Div100vh>
      <div className="flex h-full flex-col">
        {/* No intro for a game that's already over (e.g. reopened on a new
            device): returning guests go straight to the Save the Date. */}
        {!isGameWon && !isGameLost && (
          <WelcomeOverlay
            onShowInstructions={() => setIsInfoModalOpen(true)}
            puzzleDay={solutionIndex}
          />
        )}
        <Navbar
          setIsInfoModalOpen={setIsInfoModalOpen}
          setIsStatsModalOpen={setIsStatsModalOpen}
          setIsDatePickerModalOpen={setIsDatePickerModalOpen}
          setIsSettingsModalOpen={setIsSettingsModalOpen}
        />

        {!isLatestGame && (
          <div className="flex items-center justify-center">
            <ClockIcon className="h-6 w-6 stroke-gray-600 dark:stroke-gray-300" />
            <p className="text-base text-gray-600 dark:text-gray-300">
              {format(gameDate, 'd MMMM yyyy', { locale: DATE_LOCALE })}
            </p>
          </div>
        )}

        <div className="mx-auto flex w-full grow flex-col px-1 pt-2 pb-4 sm:px-6 md:max-w-7xl lg:px-8 short:pb-2 short:pt-2">
          <div className="flex grow flex-col justify-center pb-3 sm:grow-0 short:pb-2">
            <Hints
              guessCount={guesses.length}
              gameOver={isGameWon || isGameLost}
            />
            <Grid
              solution={solution}
              guesses={guesses}
              currentGuess={currentGuess}
              isRevealing={isRevealing}
              currentRowClassName={currentRowClass}
            />
          </div>
          <Keyboard
            onChar={onChar}
            onDelete={onDelete}
            onEnter={onEnter}
            solution={solution}
            guesses={guesses}
            isRevealing={isRevealing}
          />
          <InfoModal
            isOpen={isInfoModalOpen}
            handleClose={() => setIsInfoModalOpen(false)}
          />
          <SaveTheDate
            key={saveTheDateKey}
            isOpen={isSaveTheDateOpen}
            handoff={saveTheDateHandoff}
            onBack={() => {
              setStatsFromCard(true)
              setIsStatsModalOpen(true)
            }}
            onClosed={() => setIsSaveTheDateOpen(false)}
          />
          <StatsModal
            isOpen={isStatsModalOpen}
            handleClose={() => {
              setStatsFromCard(false)
              setIsStatsModalOpen(false)
            }}
            openInstantly={statsFromCard}
            solution={solution}
            guesses={guesses}
            gameStats={stats}
            solutionIndex={solutionIndex}
            solveTimeMs={getPuzzleElapsedMs(solutionIndex)}
            isLatestGame={isLatestGame}
            isGameLost={isGameLost || MOCK_STATS === 'lost'}
            isGameWon={
              isGameWon || MOCK_STATS === 'won' || MOCK_STATS === 'card'
            }
            handleOpenSaveTheDate={() => {
              setStatsFromCard(false)
              setIsStatsModalOpen(false)
              setSaveTheDateKey((k) => k + 1)
              setSaveTheDateHandoff(true)
              setIsSaveTheDateOpen(true)
            }}
            handleShareToClipboard={() => showSuccessAlert(GAME_COPIED_MESSAGE)}
            handleShareFailure={() =>
              showErrorAlert(SHARE_FAILURE_TEXT, {
                durationMs: LONG_ALERT_TIME_MS,
              })
            }
            handleMigrateStatsButton={() => {
              setIsStatsModalOpen(false)
              setIsMigrateStatsModalOpen(true)
            }}
            isHardMode={isHardMode}
            isDarkMode={isDarkMode}
            isHighContrastMode={isHighContrastMode}
            numberOfGuessesMade={
              MOCK_STATS === 'won' ? MOCK_GUESS_COUNT : guesses.length
            }
          />
          <DatePickerModal
            isOpen={isDatePickerModalOpen}
            initialDate={solutionGameDate}
            handleSelectDate={(d) => {
              setIsDatePickerModalOpen(false)
              setGameDate(d)
            }}
            handleClose={() => setIsDatePickerModalOpen(false)}
          />
          <MigrateStatsModal
            isOpen={isMigrateStatsModalOpen}
            handleClose={() => setIsMigrateStatsModalOpen(false)}
          />
          <SettingsModal
            isOpen={isSettingsModalOpen}
            handleClose={() => setIsSettingsModalOpen(false)}
            isHardMode={isHardMode}
            handleHardMode={handleHardMode}
            isDarkMode={isDarkMode}
            handleDarkMode={handleDarkMode}
            isHighContrastMode={isHighContrastMode}
            handleHighContrastMode={handleHighContrastMode}
          />
          <AlertContainer />
        </div>
      </div>
    </Div100vh>
  )
}

export default App
